/**
 * Tab 2 — Simulador: a tidy lab. Left = live weighting (shares the same in-memory
 * matrix as Configuración). Right = a hypothetical client's situation per sub-factor
 * + overrides. Top = the live composite score/nivel. Guardar persists the weighting
 * to the real matrix; Reiniciar reverts it to the last saved version.
 */
import { useMemo, useState } from 'react';
import {
  CATALOG_OVERRIDE_KEY,
  RISK_FACTOR_REFERENCE,
  RISK_SUBFACTOR_CATALOGS,
  catalogForMode,
  computeRiskScore,
  type RiskCatalogEntry,
  type RiskFactorKey,
  type RiskLevelInput,
  type RiskNivel,
  type RiskOverrides,
} from '@cumpli/shared';
import { FlaskConical, RotateCcw, Save } from 'lucide-react';
import { THRESHOLD_HEX } from '../../lib/tokens';
import { Button, Card, Pill, cn } from '../../components/ui';
import {
  CatalogSelect,
  ENTITY_MODE_COPY,
  FACTORS,
  LevelPicker,
  NIVEL_TONE,
  SIM_ROW_LABELS,
  WeightRow,
  applyBandOverrides,
  explainOf,
  familyAppliesInMode,
  subfactorAppliesInMode,
  type MatrixController,
} from './shared';

/** A fresh simulator starting point: every band at its lowest. */
const BASE_SIM_LEVELS: RiskLevelInput = {
  cliente: { profesion: 'bajo', estructura: 'bajo', antecedentes: 'bajo' },
  jurisdiccion: { nacionalidad: 'bajo', origen_destino: 'bajo', domicilio: 'bajo', listas: 'bajo' },
  canal: { canal_via: 'bajo', frecuencia: 'bajo', monto: 'bajo', instrumento: 'bajo' },
  producto: 'bajo',
};

/** Every catalog opens on its first (bajo) entry — the fresh-simulator baseline. */
const BASE_SIM_SELECTION: Record<string, string> = Object.fromEntries(
  Object.entries(RISK_SUBFACTOR_CATALOGS).map(([k, entries]) => [k, entries[0]!.key])
);

const OVERRIDES: { key: keyof RiskOverrides; label: string }[] = [
  { key: 'sanciones', label: 'Sanciones' },
  { key: 'pep', label: 'PEP' },
  { key: 'fatfNegra', label: 'FATF negra' },
  { key: 'antecedente', label: 'Antecedente' },
];

export default function Simulador({ ctrl }: { ctrl: MatrixController }) {
  const { mode, effectiveMatrix, bandOverrides, disabledForMode, userDisabled, weights, canEdit } = ctrl;

  const [levels, setLevels] = useState<RiskLevelInput>(BASE_SIM_LEVELS);
  const [overrides, setOverrides] = useState<RiskOverrides>({});
  const [selection, setSelection] = useState<Record<string, string>>(BASE_SIM_SELECTION);

  const catalogOf = (catKey: string): RiskCatalogEntry[] => {
    const off = new Set(disabledForMode[catKey] ?? []);
    return applyBandOverrides(
      catalogForMode(RISK_SUBFACTOR_CATALOGS[catKey] ?? [], mode),
      bandOverrides[catKey]
    ).filter((e) => !off.has(e.key));
  };

  const result = useMemo(
    () => computeRiskScore(effectiveMatrix, levels, overrides),
    [effectiveMatrix, levels, overrides]
  );

  const visibleFamilies = RISK_FACTOR_REFERENCE.filter(
    (fam) => familyAppliesInMode(mode, fam.key) && !userDisabled.families.includes(fam.key)
  );
  const anyModeFamilyHidden = RISK_FACTOR_REFERENCE.some((fam) => !familyAppliesInMode(mode, fam.key));
  const anyUserHidden = userDisabled.families.length > 0 || userDisabled.subfactors.length > 0;

  function setLeaf(family: keyof RiskLevelInput, key: string, v: RiskNivel) {
    if (family === 'producto') {
      setLevels((l) => ({ ...l, producto: v }));
      return;
    }
    setLevels((l) => ({ ...l, [family]: { ...(l[family] as Record<string, RiskNivel>), [key]: v } }));
  }
  function pickCatalog(catKey: string, family: keyof RiskLevelInput, leaf: string, entry: RiskCatalogEntry) {
    setSelection((s) => ({ ...s, [catKey]: entry.key }));
    setLeaf(family, leaf, entry.band);
    const overrideKey = CATALOG_OVERRIDE_KEY[catKey];
    if (overrideKey) setOverrides((o) => ({ ...o, [overrideKey]: !!entry.override }));
  }
  function resetClient() {
    setLevels(BASE_SIM_LEVELS);
    setOverrides({});
    setSelection(BASE_SIM_SELECTION);
  }

  const total = weights.cliente + weights.jurisdiccion + weights.canal + weights.producto;

  return (
    <div className="space-y-5">
      {/* header + lab controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-lg">
            <FlaskConical className="h-4 w-4 text-primary" strokeWidth={2} /> Laboratorio de riesgo
          </h2>
          <p className="mt-0.5 text-sm text-ink-soft">
            Ajusta los pesos y simula un cliente. <span className="font-semibold text-ink">Guardar</span> aplica los
            pesos a la matriz real; <span className="font-semibold text-ink">Reiniciar</span> vuelve a la última guardada.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            onClick={ctrl.resetToSaved}
            disabled={!ctrl.dirty}
            className="px-3"
            title="Revertir los pesos a la matriz guardada"
          >
            <RotateCcw className="h-4 w-4" strokeWidth={2} /> Reiniciar
          </Button>
          <Button onClick={ctrl.save} disabled={!canEdit || !ctrl.canSave || !ctrl.dirty || ctrl.saving}>
            <Save className="h-4 w-4" strokeWidth={2} /> {ctrl.saving ? 'Guardando…' : 'Guardar matriz'}
          </Button>
        </div>
      </div>

      {/* live score banner */}
      <div className="flex items-center justify-between rounded-2xl border border-line-strong bg-panel px-5 py-4 shadow-card">
        <div>
          <div className="font-mono text-4xl font-bold tabular-nums text-ink">{result.score}</div>
          <div className="mt-0.5 text-xs text-ink-faint">{result.ddc} · revisión {result.cadenciaMeses} m</div>
        </div>
        <div className="text-right">
          <Pill tone={NIVEL_TONE[result.nivel]}>{result.nivel}</Pill>
          {result.overridesFired.length > 0 && (
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-tone-red">Override → alto</div>
          )}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* left: live weighting (shared matrix) */}
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg">Ponderación (en vivo)</h3>
            <Pill tone={total === 100 ? 'green' : 'red'}>Suma {total}%</Pill>
          </div>
          <div className="space-y-3">
            {FACTORS.map((f) => {
              const famKey = f.key as RiskFactorKey;
              const ref = RISK_FACTOR_REFERENCE.find((r) => r.key === famKey);
              const familyOff =
                !familyAppliesInMode(mode, famKey) || userDisabled.families.includes(famKey);
              return (
                <WeightRow
                  key={f.key}
                  label={f.label}
                  aka={f.aka}
                  help={explainOf(ref)}
                  value={weights[f.key]}
                  disabled={!canEdit || familyOff}
                  badge={familyOff ? 'No usado' : undefined}
                  onChange={(v) => ctrl.setWeight(f.key, v)}
                />
              );
            })}
          </div>
          {total !== 100 && (
            <p className="mt-3 text-sm font-semibold text-tone-red">Las ponderaciones deben sumar 100%.</p>
          )}
          <p className="mt-3 text-xs text-ink-faint">
            Los sub-factores, umbrales e indicadores se ajustan en la pestaña Configuración.
          </p>
        </Card>

        {/* right: hypothetical client */}
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg">Situación del cliente</h3>
            <button
              type="button"
              onClick={resetClient}
              className="inline-flex items-center gap-1 text-xs font-semibold text-ink-soft hover:text-ink"
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> Limpiar
            </button>
          </div>

          <div className="space-y-3">
            {visibleFamilies.map((fam) => {
              const modeActiveSubs = fam.subfactors.filter((sf) => subfactorAppliesInMode(mode, fam.key, sf.key));
              const activeSubs = modeActiveSubs.filter(
                (sf) => !userDisabled.subfactors.includes(`${fam.key}.${sf.key}`)
              );
              const anySubHidden = modeActiveSubs.length < fam.subfactors.length;
              return (
                <div key={fam.key} className="rounded-xl border border-line p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-bold text-ink">{fam.label}</span>
                    <span className="font-mono text-xs tabular-nums text-ink-faint">
                      {Math.round(result.familyPoints[fam.key])} pts
                    </span>
                  </div>
                  {fam.subfactors.length === 0 ? (
                    (() => {
                      const catalog = catalogOf('producto');
                      return catalog.length ? (
                        <div className="flex items-start gap-2">
                          <span className="mt-1.5 w-24 shrink-0 truncate text-sm text-ink-soft">
                            {SIM_ROW_LABELS['producto']}
                          </span>
                          <CatalogSelect
                            entries={catalog}
                            value={selection['producto'] ?? catalog[0]!.key}
                            onChange={(entry) => pickCatalog('producto', 'producto', 'producto', entry)}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-ink-soft">Nivel del producto</span>
                          <LevelPicker value={levels.producto} onChange={(v) => setLeaf('producto', 'producto', v)} />
                        </div>
                      );
                    })()
                  ) : (
                    <div className="space-y-2">
                      {activeSubs.map((sf) => {
                        const catKey = `${fam.key}.${sf.key}`;
                        const catalog = catalogOf(catKey);
                        if (catalog.length) {
                          return (
                            <div key={sf.key} className="flex items-start gap-2">
                              <span className="mt-1.5 w-24 shrink-0 truncate text-sm text-ink-soft">
                                {SIM_ROW_LABELS[catKey] ?? sf.label}
                              </span>
                              <CatalogSelect
                                entries={catalog}
                                value={selection[catKey] ?? catalog[0]!.key}
                                onChange={(entry) => pickCatalog(catKey, fam.key as keyof RiskLevelInput, sf.key, entry)}
                              />
                            </div>
                          );
                        }
                        return (
                          <div key={sf.key} className="flex items-center justify-between gap-2">
                            <span className="min-w-0 truncate text-sm text-ink-soft">{sf.label}</span>
                            <LevelPicker
                              value={
                                (levels[fam.key as Exclude<keyof RiskLevelInput, 'producto'>] as Record<string, RiskNivel>)[
                                  sf.key
                                ] ?? 'bajo'
                              }
                              onChange={(v) => setLeaf(fam.key as keyof RiskLevelInput, sf.key, v)}
                            />
                          </div>
                        );
                      })}
                      {anySubHidden && (
                        <p className="rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
                          {ENTITY_MODE_COPY.transactionalNote}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {anyModeFamilyHidden && (
            <p className="mt-3 rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
              {ENTITY_MODE_COPY.transactionalNote}
            </p>
          )}
          {anyUserHidden && (
            <p className="mt-3 rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
              Algunos indicadores están desactivados en su plantilla y no cuentan en el puntaje.
            </p>
          )}

          <div className="mt-4">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-faint">Indicadores (override)</div>
            <div className="flex flex-wrap gap-2">
              {OVERRIDES.map((o) => {
                const on = !!overrides[o.key];
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setOverrides((s) => ({ ...s, [o.key]: !on }))}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                      on ? 'text-white' : 'border-line bg-panel text-ink-soft hover:text-ink'
                    )}
                    style={on ? { background: THRESHOLD_HEX.alto, borderColor: THRESHOLD_HEX.alto } : undefined}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="mt-3 text-[10px] text-ink-faint">
            Bandas por país: calibración de referencia (corte jun 2026); verifique la lista GAFI/UE vigente antes de decidir.
          </p>
        </Card>
      </div>
    </div>
  );
}
